// ============================================================
// TOGGLE ENTRIES - the attack toggles' whole arithmetic.
//
// Three things that must never disagree live here together: the record
// (AttackToggleState), the SUM the roll uses (computeToggleModifier),
// and the ITEMISATION the ledger shows as chips (computeToggleEntries).
// The anti-drift rule (dev-docs/gotchas.md) demands one source, and a
// parity test asserts the entries total exactly what the fold folds -
// over all 2^7 masks.
//
// The WRITER lives here too (applyStanceToggle): mutual exclusion is a
// rule about stances, not about buttons, and it was buried in a click
// handler until v8.299.0 - where the drum could not reach it.
//
// Pure. Zero React dependency.
// ============================================================
import { getWSP } from '../weaponSpecials';

/**
 * The toggle record's shape, owned HERE so the engine never imports
 * from components. components/AttackToggles re-exports it.
 */
export interface AttackToggleState {
  aimHalf: boolean;
  aimFull: boolean;
  charge: boolean;
  calledShot: boolean;
  allOutAttack: boolean;
  guardedAttack: boolean;
  gangUp: boolean;
}

/** No stance, no aim, nothing called - the record every session starts from. */
export const EMPTY_TOGGLES: AttackToggleState = {
  aimHalf: false,
  aimFull: false,
  charge: false,
  calledShot: false,
  allOutAttack: false,
  guardedAttack: false,
  gangUp: false,
};

/**
 * Toggles that cannot be held at once. You aim once, at one length; you
 * take one stance. Switching one ON releases its siblings; switching it
 * OFF frees nobody, which is why this is not a radio group.
 */
const EXCLUSIVE_GROUPS: (keyof AttackToggleState)[][] = [
  ['aimHalf', 'aimFull'],
  ['charge', 'allOutAttack', 'guardedAttack'],
];

/**
 * Set one toggle and settle the exclusions, returning a NEW record.
 * The single writer for every surface that engages a stance - the pill
 * row, the drum, a macro - so the rule cannot be half-implemented twice.
 *
 * SETTING rather than flipping is what the drum's engage-and-fire
 * plates need (v8.301.0): "attack all-out" must engage All-Out, not
 * switch it off because it happened to be on already.
 */
export function setStanceToggle(
  toggles: AttackToggleState,
  key: keyof AttackToggleState,
  on: boolean,
): AttackToggleState {
  const next = { ...toggles, [key]: on };
  if (!on) return next;
  const group = EXCLUSIVE_GROUPS.find((g) => g.includes(key));
  if (group) for (const other of group) if (other !== key) next[other] = false;
  return next;
}

/** Flip one toggle - the pill row's press, and the drum's for an `engage`. */
export function applyStanceToggle(toggles: AttackToggleState, key: keyof AttackToggleState): AttackToggleState {
  return setStanceToggle(toggles, key, !toggles[key]);
}

export interface ToggleEntry {
  /** The AttackToggleState key, so a chip's remove can clear it. */
  key: keyof AttackToggleState;
  label: string;
  value: number;
}

/**
 * The stance family (v8.281.0): per-weapon riders on the stance
 * toggles' values. One derivation shared by the fold
 * (computeToggleModifier), the itemisation (below) and the narrative
 * mirror (calcSkillBreakdown) - the anti-drift rule, again.
 */
export interface StanceDeltas {
  /** Sighted(X): Aim grants +X more (wasted with the aim by Inaccurate). */
  aim: number;
  /** Impetus(X): Charge grants +X more (Berserk included). */
  charge: number;
  /** Reckless(X): All-Out Attack grants +X more. */
  allOut: number;
  /** Poised(X): the Guarded penalty shrinks by X - to zero, never a bonus. */
  guarded: number;
}

export function stanceDeltas(specialRules: string[]): StanceDeltas {
  return {
    aim: getWSP(specialRules, 'Sighted'),
    charge: getWSP(specialRules, 'Impetus'),
    allOut: getWSP(specialRules, 'Reckless'),
    guarded: getWSP(specialRules, 'Poised'),
  };
}

/** The Guarded Attack penalty after Poised - floored at 0. */
export function guardedPenalty(deltas: StanceDeltas): number {
  return -Math.max(10 - deltas.guarded, 0);
}

export function computeToggleEntries(
  toggles: AttackToggleState,
  calledShotReduction: number,
  hasBerserkCharge: boolean,
  isMelee: boolean,
  inaccurate = false,
  specialRules: string[] = [],
  /** The target wears a `snap` exposure (v8.356.0): the aim is refused, not wasted - same zero. */
  noAim = false,
): ToggleEntry[] {
  const d = stanceDeltas(specialRules);
  const refused = aimRefusal(noAim, inaccurate);
  const entries = [
    toggles.aimHalf ? aimEntry('aimHalf', { label: 'Aim (Half)', value: 10 + d.aim }, refused) : null,
    toggles.aimFull ? aimEntry('aimFull', { label: 'Aim (Full)', value: 20 + d.aim }, refused) : null,
    ...(isMelee ? meleeEntries(toggles, hasBerserkCharge, d) : []),
    toggles.calledShot
      ? { key: 'calledShot' as const, label: 'Called Shot', value: -Math.max(20 - calledShotReduction, 0) }
      : null,
  ];
  return entries.filter((e): e is ToggleEntry => e !== null);
}

/** Why an aim gains nothing, or null when it counts. A refusal outranks a waste. */
function aimRefusal(noAim: boolean, inaccurate: boolean): string | null {
  if (noAim) return 'Aim refused (no aiming at them)';
  return inaccurate ? 'Aim wasted (Inaccurate)' : null;
}

/** An aim chip: its full value, or zero with the reason it earns nothing. */
function aimEntry(
  key: 'aimHalf' | 'aimFull',
  earned: { label: string; value: number },
  refused: string | null,
): ToggleEntry {
  return refused ? { key, label: refused, value: 0 } : { key, ...earned };
}

/** The melee-only chips, in the order they are listed. */
function meleeEntries(toggles: AttackToggleState, hasBerserkCharge: boolean, d: StanceDeltas): (ToggleEntry | null)[] {
  const charge: ToggleEntry = hasBerserkCharge
    ? { key: 'charge', label: 'Berserk Charge', value: 20 + d.charge }
    : { key: 'charge', label: 'Charge', value: 10 + d.charge };
  return [
    toggles.charge ? charge : null,
    toggles.gangUp ? { key: 'gangUp', label: 'Gang Up', value: 10 } : null,
    toggles.allOutAttack ? { key: 'allOutAttack', label: 'All-Out Attack', value: 20 + d.allOut } : null,
    toggles.guardedAttack ? { key: 'guardedAttack', label: 'Guarded Attack', value: guardedPenalty(d) } : null,
  ];
}

/**
 * The SUM the attack roll uses: the total of `computeToggleEntries`
 * above, so the chips and the number agree by construction (the parity
 * test over all 2^7 masks still pins it).
 */
export function computeToggleModifier(
  toggles: AttackToggleState,
  calledShotReduction: number = 0,
  hasBerserkCharge: boolean = false,
  isMelee: boolean = true,
  inaccurate: boolean = false,
  specialRules: string[] = [],
  /** The target wears a `snap` exposure (v8.356.0). */
  noAim: boolean = false,
): number {
  // The SUM OF THE CHIPS, so the number and the itemisation cannot
  // disagree. Official Inaccurate (v8.198.3) and a `snap` refusal are
  // chips worth 0 - the action is spent, the bonus never arrives, and
  // Sighted rides the aim, so it is wasted with it. The stance family
  // (v8.281.0) bends each value per weapon through `stanceDeltas`.
  return computeToggleEntries(
    toggles,
    calledShotReduction,
    hasBerserkCharge,
    isMelee,
    inaccurate,
    specialRules,
    noAim,
  ).reduce((total, entry) => total + entry.value, 0);
}
